<template>
  <main class="flex flex-col md:!pb-12">
    <UiFixedHeader>
      <span class="line-clamp-1">
        {{ event?.title }}
      </span>
    </UiFixedHeader>

    <div class="relative">
      <div class="relative h-fit">
        <UiContainer tag="section" class="!rounded-none !p-0 lg:!px-7">
          <GoBack class="!absolute left-3 top-5 z-20 !bg-transparent md:hidden">
            <div
              class="absolute inset-0 size-full rounded-full bg-primary opacity-45 backdrop-blur-lg"
            />
            <UiIcon name="chevron-down" class="relative size-4 rotate-90 text-icon-secondary" />
          </GoBack>
          <section class="md:mb-3">
            <EventBaseInfo
              :value="event"
              :loading="!initialized"
              @poster-button-visibility-change="onPosterButtonVisibilityChange"
            />
          </section>
        </UiContainer>
        <UiContainer tag="section" rounded="top" class="relative z-10 -mt-6 bg-primary md:mt-0">
          <EventDescription :value="event" :loading="!initialized" :class="isSmAndDown && 'pt-3'" />
        </UiContainer>
      </div>

      <template v-if="showTimeTablePreview">
        <UiHorizontalRule />
        <EventTimetablePreview
          :id="GlobalIds['eventTimetablePreview']"
          :value="event"
          :loading="!initialized"
          class="py-2 lg:py-0"
          @open-timetable="openTimetable"
        />
      </template>

      <template v-if="showGallery">
        <UiHorizontalRule />
        <UiContainer>
          <EventGallery
            v-if="showGallery"
            :value="event"
            :loading="!initialized"
            class="relative col-span-5 py-2 lg:order-3 lg:py-0"
          />
        </UiContainer>
      </template>
    </div>

    <UiHorizontalRule />

    <template v-if="showVenues">
      <EventVenues
        v-if="showVenues"
        :value="event"
        :loading="!initialized"
        class="py-2 pb-0 lg:py-0"
      />
      <UiHorizontalRule />
    </template>

    <template v-if="compilations">
      <template v-for="(compilation, index) in compilations" :key="compilation.slug">
        <CompilationWidget
          type="list"
          :size="index > 0 && !(index % 2) ? 'small' : 'big'"
          :value="compilation"
          class="py-2 lg:py-0"
          :grid="gridSize"
        />
        <UiHorizontalRule v-if="index < compilations.length - 1" />
      </template>
    </template>

    <UiContainer
      v-if="event && isLgAndDown && !isPosterButtonVisible && initialized"
      class="!sticky bottom-20 z-30 mt-4 w-full md:bottom-4 lg:hidden"
    >
      <TransitionFade>
        <UseSeanceTimetableHelper
          v-if="event"
          v-model:visible="isHelperButtonVisible"
          :value="event"
          class="w-full"
        />
      </TransitionFade>
    </UiContainer>

    <div v-if="showPageCompilations">
      <UiHorizontalRule />
      <CompilationsForPage
        :value="pageCompilations"
        :loading="!pageCompilationsInitialized"
        class="py-2 lg:py-0"
      />
    </div>

    <UiDialog
      v-if="showTimeTablePreview"
      :model-value="isTimetableVisible"
      :position="isLgAndUp ? 'middle' : 'bottom'"
      :fit="isLgAndUp"
      :swipe-close="isLgAndDown"
      @update:model-value="closeTimetable"
    >
      <template #default="{ numberHeight }">
        <div
          :style="{ height: `${numberHeight - 120}px` }"
          class="relative size-full rounded-t-3xl bg-primary lg:w-screen lg:max-w-[744px] lg:rounded-3xl"
        >
          <button
            type="button"
            class="!absolute right-6 top-2 z-20 flex size-10 items-center justify-center !p-0 lg:-right-9 lg:-top-3 lg:size-9 lg:min-w-9"
            @click="closeTimetable"
          >
            <UiIcon name="x-mark" class="size-4.5 md:size-6" />
          </button>
          <EventTimetable
            :value="event"
            :loading="!initialized"
            class="scrollbar-hidden h-full overflow-hidden rounded-t-3xl lg:rounded-3xl"
          />
        </div>
      </template>
    </UiDialog>
  </main>
</template>

<script setup lang="ts">
import { TransitionFade } from '@morev/vue-transitions'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { type LocationQuery, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { CompilationsForPage } from '@/3_widgets/Compilation'
import { EventBaseInfo, EventDescription } from '@/3_widgets/Event'
import { GoBack } from '@/4_features/Navigation'
import { UseSeanceTimetableHelper } from '@/4_features/Seance'
import { useCompilation, useCompilationGrid } from '@/5_entities/Compilation'
import { useReloadOnConfigurationUpdate } from '@/5_entities/Configuration'
import { useEvent, type Event } from '@/5_entities/Event'
import { useQueryParamsOpenState } from '@/6_shared/composables'
import { GlobalIds } from '@/6_shared/config'
import { useBreakpoint } from '@/6_shared/lib'
import type { TimetableFilterMode } from '@/6_shared/model'
import { UiContainer, UiDialog, UiFixedHeader, UiHorizontalRule, UiIcon } from '@/6_shared/ui'

const EventTimetablePreview = defineAsyncComponent(
  async () => (await import('@/3_widgets/Event')).EventTimetablePreview
)
const EventTimetable = defineAsyncComponent(
  async () => (await import('@/3_widgets/Event')).EventTimetable
)
const EventGallery = defineAsyncComponent(
  async () => (await import('@/3_widgets/Event')).EventGallery
)
const EventVenues = defineAsyncComponent(
  async () => (await import('@/3_widgets/Event')).EventVenues
)
const CompilationWidget = defineAsyncComponent(
  async () => (await import('@/3_widgets/Compilation')).CompilationWidget
)

const { getEvent } = useEvent()
const { event, eventInitialized: initialized } = storeToRefs(useEvent())
const { gridSize } = useCompilationGrid()
const {
  lg: [isLgAndUp, isLgAndDown],
  md: [isMdAndUp],
  sm: [_, isSmAndDown]
} = useBreakpoint()

const isTimetableVisible = ref(false)

const route = useRoute()
const router = useRouter()

const { setParamInRouteQuery } = useQueryParamsOpenState('timetable', () => {
  if (isTimetableVisible.value && !route.query?.timetable) {
    isTimetableVisible.value = false
  }
  if (!isTimetableVisible.value && route.query?.timetable) {
    isTimetableVisible.value = true
  }
})

const openTimetable = (_eventId: Event['id'], mode: TimetableFilterMode) => {
  isTimetableVisible.value = true
  setParamInRouteQuery(mode)
}

const closeTimetable = () => {
  isTimetableVisible.value = false
  router.back()
}

const initPage = (id: number, query: LocationQuery) => {
  getEvent(Number(id))
  query.timetable && openTimetable(id, query.timetable as TimetableFilterMode)
  getCompilationsPack(['eventpage', Number(id)].join('/'))
  getPageCompilations('eventPage', 'eventpage', isMdAndUp.value ? 9 : 10)
}

useReloadOnConfigurationUpdate(() => initPage(Number(route.params.id), route.query))

onBeforeRouteUpdate(({ params: { id: toId }, query }, { params: { id: fromId } }) => {
  if (fromId !== toId) {
    initPage(Number(toId as string), query)
  }
})

onMounted(() => {
  const { params, query } = useRoute()
  initPage(Number(params.id), query)
})

const showTimeTablePreview = computed(
  () => !initialized.value || (event.value && event.value.seances.length > 1)
)

const showGallery = computed(
  () =>
    !initialized.value ||
    (event.value && Array.isArray(event.value.gallery) && event.value.gallery.length)
)

const showVenues = computed(
  () =>
    !initialized.value ||
    (event.value && Array.isArray(event.value.venues) && event.value.venues.length)
)

// const hasNoImages = computed(() => {
//   return !event.value?.image && !event.value?.imageMobile
// })
//
// const containerRounded = computed(() => (isSmAndDown.value ? 'none' : 'top'))

//compilations
const { getCompilationsPack } = useCompilation()
const { compilations } = storeToRefs(useCompilation())

const isPosterButtonVisible = ref(true)
const isHelperButtonVisible = ref(false)
const onPosterButtonVisibilityChange = (visible: boolean) => {
  isPosterButtonVisible.value = visible
  isHelperButtonVisible.value = !visible
}

const { getPageCompilations } = useCompilation()
const { pageCompilations, pageCompilationsInitialized, showPageCompilations } =
  storeToRefs(useCompilation())
</script>
